import ExcelJS from "exceljs";

const SHEET_NAME = "Estudio de viabilidad";
const COLUMNS = [
    { header: "Número documento", key: "numeroDocumento", width: 18 },
    { header: "Apellido", key: "apellido", width: 20 },
    { header: "Consulta", key: "consulta", width: 14 },
    { header: "Status", key: "status", width: 14 },
    { header: "Valor aprobado", key: "valorAprobado", width: 18 },
];

const LIGHT_RED = "FFFFCDD2";
const LIGHT_GREEN = "FFC8E6C9";
const RED_FONT = "FFB71C1C";
const GREEN_FONT = "FF2E7D32";
const HEADER_FILL = "FFE0E0E0";

/**
 * Genera y descarga un archivo Excel con los resultados del estudio de viabilidad.
 * Aplica formato: filas rechazadas en rojo claro, aprobadas en verde claro;
 * celda Status con texto rojo/verde según corresponda.
 * @param {Array<{numeroDocumento: string, apellido: string, consulta: string, status: string, valorAprobado: number}>} results
 * @param {string} [filename] - Nombre del archivo sin extensión
 * @returns {Promise<void>}
 */
export async function exportViabilityToExcel(results, filename) {
    if (!Array.isArray(results) || results.length === 0) {
        return Promise.reject(new Error("No hay datos para exportar"));
    }

    const workbook = new ExcelJS.Workbook();
    workbook.creator = "Prami";
    workbook.created = new Date();

    const sheet = workbook.addWorksheet(SHEET_NAME, {
        views: [{ state: "frozen", ySplit: 1 }],
    });

    sheet.columns = COLUMNS.map((col) => ({
        header: col.header,
        key: col.key,
        width: col.width,
    }));

    const headerRow = sheet.getRow(1);
    headerRow.font = { bold: true };
    headerRow.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: HEADER_FILL },
    };
    headerRow.alignment = { horizontal: "center", vertical: "middle" };

    const STATUS_COLUMN_INDEX = 4;

    results.forEach((row) => {
        const data = {
            numeroDocumento: row.numeroDocumento ?? "",
            apellido: row.apellido ?? "",
            consulta: row.consulta ?? "",
            status: row.status ?? "",
            valorAprobado:
                row.valorAprobado != null ? Number(row.valorAprobado) : "",
        };
        const excelRow = sheet.addRow(data);
        const status = String(row.status || "").toLowerCase();
        const isRechazado = status === "rechazado";
        const isAprobado = status === "aprobado";

        excelRow.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: {
                argb: isRechazado ? LIGHT_RED : isAprobado ? LIGHT_GREEN : "FFFFFFFF",
            },
        };
        excelRow.alignment = { horizontal: "center", vertical: "middle" };

        const statusCell = excelRow.getCell(STATUS_COLUMN_INDEX);
        if (isRechazado) {
            statusCell.font = { bold: true, color: { argb: RED_FONT } };
        } else if (isAprobado) {
            statusCell.font = { bold: true, color: { argb: GREEN_FONT } };
        }
    });

    const defaultName =
        filename ||
        `estudio-viabilidad-${new Date().toISOString().slice(0, 10)}`;
    const fileName = `${defaultName}.xlsx`;

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
}
